import React, { useCallback, useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, Check } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
export const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
  { key: "last14", label: "Last 14 days" },
  { key: "last30", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "custom", label: "Custom range" },
];

export function rangeForPreset(key, custom) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const shift = (base, delta) => { const x = new Date(base); x.setDate(x.getDate() + delta); return x; };
  switch (key) {
    case "today": return { from: iso(today), to: iso(today) };
    case "yesterday": { const y = shift(today, -1); return { from: iso(y), to: iso(y) }; }
    case "last7": return { from: iso(shift(today, -6)), to: iso(today) };
    case "last14": return { from: iso(shift(today, -13)), to: iso(today) };
    case "last30": return { from: iso(shift(today, -29)), to: iso(today) };
    case "this_month": return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(today) };
    case "last_month": {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: iso(first), to: iso(last) };
    }
    case "custom":
      return { from: custom?.from || iso(today), to: custom?.to || iso(today) };
    default:
      return { from: iso(shift(today, -13)), to: iso(today) };
  }
}

export function useDateRange(storageKey = "dash-range", initialPreset = "last14") {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { preset: initialPreset, custom: null };
  });

  const persist = useCallback((next) => {
    setState(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  }, [storageKey]);

  const { from, to } = rangeForPreset(state.preset, state.custom);
  return {
    preset: state.preset,
    custom: state.custom,
    from,
    to,
    setPreset: (p) => persist({ preset: p, custom: state.custom }),
    setCustom: (c) => persist({ preset: "custom", custom: c }),
  };
}

export function rangeLabel(range) {
  if (range.preset === "custom") return `${range.from} → ${range.to}`;
  return (PRESETS.find((p) => p.key === range.preset) || {}).label || "Select range";
}

export function DateRangeFilter({ range, className }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(
    range.preset === "custom" && range.custom
      ? { from: new Date(`${range.custom.from}T00:00:00`), to: new Date(`${range.custom.to}T00:00:00`) }
      : undefined
  );

  const pick = (key) => {
    if (key === "custom") { range.setPreset("custom"); return; }
    range.setPreset(key);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid="date-range-trigger"
          className={`gap-2 border-white/[0.1] bg-[#141417] text-foreground hover:bg-white/[0.05] ${className || ""}`}
        >
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="truncate max-w-[190px] text-xs sm:text-sm">{rangeLabel(range)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0 border-white/[0.1] bg-[#141417]" data-testid="date-range-popover">
        <div className="flex flex-col sm:flex-row">
          <div className="flex min-w-[160px] flex-col gap-0.5 border-b border-white/[0.08] p-2 sm:border-b-0 sm:border-r">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`date-preset-${p.key}`}
                onClick={() => pick(p.key)}
                className={`flex items-center justify-between rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                  range.preset === p.key ? "bg-white/[0.08] text-white" : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
                }`}
              >
                {p.label}
                {range.preset === p.key && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
          {range.preset === "custom" && (
            <div className="p-2">
              <Calendar
                mode="range"
                numberOfMonths={1}
                selected={sel}
                onSelect={(r) => {
                  setSel(r);
                  if (r?.from && r?.to) {
                    range.setCustom({ from: iso(r.from), to: iso(r.to) });
                    setOpen(false);
                  }
                }}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
